import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { amountToWordsSoles } from './amountToWords';
import { formatPrintDate } from './dateUtils';
import { ITEMS_TABLE, PDF_COL_WIDTHS_6, PRINT_MARGIN_MM } from './entryGuidePrintLayout';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = PRINT_MARGIN_MM;
const HEADER_FONT = 7.5;

const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtUnitPrice = (p) =>
  parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const fmtQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

function formatDate(value) {
  return formatPrintDate(value);
}

function docTypeLabel(entity) {
  if (entity?.document_type === 'ruc') return 'RUC';
  if (entity?.document_type === 'dni') return 'DNI';
  if (entity?.document_type) return String(entity.document_type).toUpperCase();
  return 'Documento';
}

function measureSegments(doc, segments) {
  let width = 0;
  segments.forEach((seg) => {
    doc.setFont('helvetica', seg.style === 'label' ? 'bold' : 'normal');
    doc.setFontSize(HEADER_FONT);
    width += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return width;
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(HEADER_FONT);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return x;
}

function drawStatusBadge(doc, x, y, statusText, tone) {
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  const padX = 2;
  const textW = doc.getTextWidth(statusText);
  const badgeW = textW + padX * 2;
  const badgeH = 3.8;

  const tones = {
    issued: { fill: [209, 250, 229], text: [6, 95, 70] },
    cancelled: { fill: [254, 226, 226], text: [153, 27, 27] },
  };
  const colors = tones[tone] || tones.issued;

  doc.setFillColor(...colors.fill);
  doc.setTextColor(...colors.text);
  doc.roundedRect(x, y - badgeH + 1.2, badgeW, badgeH, 1.2, 1.2, 'F');
  doc.text(statusText, x + padX, y);
  return x + badgeW + 2;
}

function drawSectionTitle(doc, margin, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...TEAL);
  doc.text(title.toUpperCase(), margin, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 1.2, margin + doc.getTextWidth(title.toUpperCase()), y + 1.2);
}

function buildPaymentCondition(quote) {
  if (quote.payment_condition_type === 'cash') return 'Al contado';
  if (quote.payment_condition_type === 'days') {
    return `Cada ${quote.payment_condition_days} días`;
  }
  if (quote.payment_condition_type === 'date' && quote.payment_condition_date) {
    return `Fecha específica: ${formatDate(quote.payment_condition_date)}`;
  }
  return '-';
}

function getItemDescription(item) {
  if (item.item_type === 'service' || item.service_id) {
    return item.service?.name || item.description || 'Servicio';
  }
  return item.product?.name || item.description || 'Producto';
}

function buildTableRows(quote) {
  return (quote.items || []).map((item) => [
    getItemDescription(item),
    item.unit_of_measure || item.product?.unit_of_measure || item.service?.unit_of_measure || 'UND',
    item.project ? `${item.project.code} - ${item.project.name}` : '-',
    fmtQty(item.quantity),
    `S/ ${fmtUnitPrice(item.unit_price)}`,
    `S/ ${fmtMoney(item.subtotal ?? item.total)}`,
  ]);
}

function getAmountsRightX(doc) {
  const subtotalCol = doc.lastAutoTable?.table?.columns?.[5];
  if (subtotalCol) return subtotalCol.x + subtotalCol.width - 2;

  const w = PDF_COL_WIDTHS_6;
  return MARGIN + w.description + w.unit + w.project + w.quantity + w.unitPrice + w.subtotal - 2;
}

function drawFooter(doc, quote, startY, pageWidth, pageHeight, amountsRightX) {
  const contentWidth = pageWidth - MARGIN * 2;
  const obsWidth = contentWidth * 0.52;
  const totalsWidth = contentWidth * 0.44;
  const gap = contentWidth - obsWidth - totalsWidth;
  const footerHeight = 22;

  let y = startY;
  if (y + footerHeight > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 4;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...TEAL);
  doc.text('Observaciones:', MARGIN, y + 4);

  if (quote.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    const noteLines = doc.splitTextToSize(String(quote.notes), obsWidth);
    doc.text(noteLines.slice(0, 4), MARGIN, y + 8);
  }

  const totalsX = MARGIN + obsWidth + gap;
  let ty = y + 4;
  const lineH = 4;

  const drawTotalLine = (label, value, { bold = false } = {}) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(HEADER_FONT);
    doc.setTextColor(...(bold ? TEAL : GRAY_MUTED));
    doc.text(label, totalsX, ty);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...(bold ? TEAL : GRAY));
    doc.text(value, amountsRightX, ty, { align: 'right' });
    ty += lineH;
  };

  drawTotalLine('Base:', `S/ ${fmtMoney(quote.base_imponible)}`);
  drawTotalLine('IGV:', `S/ ${fmtMoney(quote.igv_amount)}`);
  if (parseFloat(quote.no_gravados || 0) > 0) {
    drawTotalLine('No grav.:', `S/ ${fmtMoney(quote.no_gravados)}`);
  }
  drawTotalLine('Total:', `S/ ${fmtMoney(quote.total_amount)}`, { bold: true });

  const words = amountToWordsSoles(quote.total_amount);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6.5);
  doc.setTextColor(...GRAY_MUTED);
  const wordLines = doc.splitTextToSize(`Son: ${words}`, amountsRightX - totalsX);
  doc.text(wordLines.slice(0, 2), amountsRightX, ty + 0.5, { align: 'right' });
}

export async function downloadSalesQuotePdf(quote) {
  if (!quote) {
    throw new Error('No hay datos de la cotización para generar el PDF');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let y = MARGIN + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...TEAL);
  doc.text('COTIZACIÓN', pageWidth / 2, y, { align: 'center' });

  y += 3;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 4;

  const statusText = quote.status === 'cancelled' ? 'Cancelada' : 'Emitida';
  const statusTone = quote.status === 'cancelled' ? 'cancelled' : 'issued';

  const line1 = [
    { text: 'Cotización:', style: 'label' },
    { text: quote.code || '-', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fecha:', style: 'label' },
    { text: formatDate(quote.issue_date) || '—', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Válida hasta:', style: 'label' },
    { text: quote.valid_until ? formatDate(quote.valid_until) : '—', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Estado:', style: 'label' },
  ];
  const estadoX = MARGIN + measureSegments(doc, line1);
  drawSegments(doc, MARGIN, y, line1);
  drawStatusBadge(doc, estadoX, y, statusText, statusTone);

  y += 4;
  const clientLine = [
    { text: 'Cliente:', style: 'label' },
    { text: quote.client?.name || 'N/A', style: 'value' },
  ];
  if (quote.client?.document_number) {
    clientLine.push(
      { text: '·', style: 'sep' },
      { text: `${docTypeLabel(quote.client)}:`, style: 'label' },
      { text: quote.client.document_number, style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, clientLine);

  y += 4;
  const posLabel = quote.point_of_sale
    ? [quote.point_of_sale.code, quote.point_of_sale.name].filter(Boolean).join(' — ')
    : '—';
  drawSegments(doc, MARGIN, y, [
    { text: 'Punto de venta:', style: 'label' },
    { text: posLabel, style: 'value' },
  ]);

  y += 4;
  drawSegments(doc, MARGIN, y, [
    { text: 'Condición de pago:', style: 'label' },
    { text: buildPaymentCondition(quote), style: 'value' },
  ]);

  const scheduleParts = [];
  if (quote.delivery_date) scheduleParts.push(`Entrega: ${formatDate(quote.delivery_date)}`);
  if (quote.service_start_date) scheduleParts.push(`Inicio serv.: ${formatDate(quote.service_start_date)}`);
  if (quote.service_end_date) scheduleParts.push(`Fin serv.: ${formatDate(quote.service_end_date)}`);
  if (scheduleParts.length > 0) {
    y += 4;
    drawSegments(doc, MARGIN, y, [
      { text: 'Plazos:', style: 'label' },
      { text: scheduleParts.join(' · '), style: 'value' },
    ]);
  }

  y += 3;
  drawSectionTitle(doc, MARGIN, y, 'Items de la cotización');
  y += 4;

  const tableBody = buildTableRows(quote);
  if (tableBody.length === 0) {
    tableBody.push(['Sin ítems registrados', '—', '—', '—', '—', '—']);
  }

  const w = PDF_COL_WIDTHS_6;

  autoTable(doc, {
    startY: y,
    head: [['Descripción', 'Unidad', 'Proyecto', 'Cant.', 'P. Unit', 'Subtotal']],
    body: tableBody,
    margin: { left: MARGIN, right: MARGIN, top: MARGIN, bottom: 10 },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: ITEMS_TABLE.bodyFontSize,
      cellPadding: ITEMS_TABLE.cellPadding,
      minCellHeight: ITEMS_TABLE.minCellHeight,
      textColor: GRAY,
      lineColor: [229, 231, 235],
      lineWidth: 0.15,
      valign: 'middle',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: ITEMS_TABLE.headFontSize,
      halign: 'left',
      cellPadding: { top: 1.2, right: 2, bottom: 1.2, left: 2 },
    },
    columnStyles: {
      0: { cellWidth: w.description },
      1: { cellWidth: w.unit, halign: 'center' },
      2: { cellWidth: w.project },
      3: { cellWidth: w.quantity, halign: 'right' },
      4: { cellWidth: w.unitPrice, halign: 'right' },
      5: { cellWidth: w.subtotal, halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    showHead: 'everyPage',
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...GRAY_LIGHT);
      doc.text(
        `Cotización ${quote.code || ''} — Pág. ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' },
      );
    },
  });

  const tableEndY = doc.lastAutoTable.finalY + 8;
  const amountsRightX = getAmountsRightX(doc);
  drawFooter(doc, quote, tableEndY, pageWidth, pageHeight, amountsRightX);

  const safeCode = String(quote.code || quote.id || 'cotizacion').replace(/[^\w.-]+/g, '_');
  doc.save(`Cotizacion_${safeCode}.pdf`);
}
